import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";

export default function Login() {
  const [form, setForm] = useState({ email: "", password: "", remember: true });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      setUser(data);
      toast.success(`Welcome back, ${data.name}!`);
      const next = location.state?.next;
      navigate(data.role === "admin" ? "/admin" : next || "/");
    } catch (err) {
      setError(fmtErr(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Brand /></div>
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-6 sm:p-8" data-testid="login-card">
          <h1 className="text-2xl font-bold font-heading text-slate-900">Welcome back</h1>
          <p className="mt-1.5 text-sm text-slate-600">Login to your Equinox 4 Review account.</p>
          {error && <div data-testid="login-error" className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</div>}
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
              <input data-testid="login-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="you@example.com" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
              <input data-testid="login-password-input" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="••••••••" />
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input data-testid="login-remember-checkbox" type="checkbox" checked={form.remember} onChange={(e) => setForm({ ...form, remember: e.target.checked })} className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                Remember me
              </label>
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm font-medium text-blue-700 hover:text-blue-800">Forgot password?</Link>
            </div>
            <button type="submit" data-testid="login-submit-btn" disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 transition-colors shadow-sm">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Login
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-slate-600">
            New here? <Link to="/register" data-testid="login-register-link" className="font-semibold text-blue-700 hover:text-blue-800">Create an account</Link>
          </p>
          <div className="mt-5 pt-4 border-t border-slate-100 text-center">
            <Link to="/admin/login" data-testid="admin-portal-link" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors">
              Are you an admin? Go to Admin Portal →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
